'use client'

import _ from 'lodash'
import { twMerge } from 'tailwind-merge'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import NextLink from 'next/link'
import { deleteCookie } from 'cookies-next/client'
import { usePathname, redirect } from 'next/navigation'
import { ArrowRightEndOnRectangleIcon } from '@heroicons/react/24/outline'

import { useGlobalStore } from '@/store/provider'
import { pages } from '@/settings'
import { Avatar, LinkButton } from '@/components'

dayjs.extend(relativeTime)

type NavItemProps = {
  icon?: React.ReactNode
  name: string
  href: string
  count?: number
}

export function NavMy() {
  const pathname = usePathname()
  const { user, setUser, setToast } = useGlobalStore((state) => state)

  function signOut() {
    setUser(null, null)
    deleteCookie('access_token')
    deleteCookie('refresh_token')
    setToast({ type: 'success', message: 'Successfully signed out' })
    redirect('/')
  }

  const NavItem = ({ icon, name, href, count }: NavItemProps) => {
    return (
      <li>
        <NextLink hrefLang='en' href={href} className={
          twMerge('nav-item', pathname === href && 'bg-zinc-100 dark:bg-zinc-800')
        }>
          <span className='flex items-center gap-1.5'>
            {icon}
            {name}
          </span>
          {
            (!_.isUndefined(count)) &&
            <span className='nav-count'>{count}</span>
          }
        </NextLink>
      </li>
    )
  }

  if (_.isEmpty(user)) {
    return <></>
  }

  const MyInfo = () => {
    return (
      <div className='w-full flex flex-col items-center text-sm/6 mb-4 relative'>
        <NextLink hrefLang='en' href={pages.myProfile.href}>
          <Avatar className='size-24' photo_url={user?.photo_url} name={`${user?.first_name} ${user?.last_name}`} />
        </NextLink>
        <div className='text-lg font-bold truncate mt-3 mb-1'>
          {
            _.isEmpty(user?.first_name)
              ? user?.email
              : `${user?.first_name} ${user?.last_name}`
          }
        </div>
        {
          !_.isEmpty(user?.location) &&
          <div className='truncate font-light'>
            {user?.location?.city}, {user?.location?.state_abbr} {user?.location?.zip_code}
          </div>
        }
        <div className='truncate font-light'>
          Joined {dayjs(user?.date_create).fromNow()}
        </div>
        {
          _.isEmpty(user?.location) &&
          <div className='mt-4'>
            <LinkButton variant='primary' className='rounded-full px-6' href={pages.myProfile.href} icon='edit' label='Update Profile' />
          </div>
        }
      </div>
    )
  }

  return (
    <>
      <MyInfo />
      <div className='w-full border-t-2 border-zinc-200 dark:border-zinc-600' />
      <ul role='list' className='divide-y dark:divide-zinc-700'>
        <NavItem {...pages.myCruz} />
        <NavItem {...pages.mySavedSearch} count={user?.statistic?.saved_search || 0} />
        <NavItem {...pages.mySavedVehicle} count={user?.statistic?.saved_vehicle || 0} />
        <NavItem {...pages.myInquiries} count={user?.statistic?.inquiry_count || 0} />
        <NavItem {...pages.myVehicles} count={user?.statistic?.vehicle?.all || 0} />
        <NavItem {...pages.postVehicle} />
        <NavItem {...pages.mySpotted} count={user?.statistic?.spotted_count || 0} />
        <NavItem {...pages.postSpotted} />
        <NavItem {...pages.myProfile} />
        <NavItem {...pages.myPassword} />
        <li className='py-2'>
          <button type='button' className='nav-item' onClick={signOut}>
            <span className='flex items-center gap-1.5'>
              <ArrowRightEndOnRectangleIcon className='w-4' />
              Sign Out
            </span>
          </button>
        </li>
      </ul>
    </>
  )

}

